/**
 * Splitting a chapter into the paragraphs a picture can be anchored to
 * (ADR-0038).
 *
 * This is the twin of `Paragraphs` in `backend/internal/illustration/anchor.go`
 * and has to agree with it: the server stores an anchor as the hash of a
 * paragraph's trimmed text and hands back the *index* it resolved to, and this
 * is what turns that index back into a place on the page. A split that
 * disagreed would put pictures between the wrong words.
 *
 * A paragraph is a non-blank line. Blank lines are spacing — the same rule the
 * reader's sentence splitter uses (`player/sentences.ts`), so a picture "after
 * paragraph 3" lands after the same words everywhere.
 */

export interface Paragraph {
  /** Counts non-blank lines from zero: the index the server speaks in. */
  index: number;
  /** The trimmed text, which is what the anchor is taken over. */
  text: string;
  /** The line as authored, so joining the pieces reproduces the chapter. */
  raw: string;
  /**
   * Where `text` sits in the body, in UTF-16 code units — the units a
   * textarea and `String.slice` use, not the server's bytes. Editing a
   * paragraph in place replaces exactly this range (ADR-0051).
   */
  start: number;
  end: number;
}

/**
 * Go's `strings.TrimSpace`, which is what the server trims with: Unicode
 * White_Space plus U+0085. JavaScript's `trim` differs on two characters — it
 * keeps U+0085 and strips U+FEFF — and a paragraph that trims differently on
 * the two sides hashes differently, so its picture never finds it.
 */
const SPACE =
  "\\t\\n\\v\\f\\r \\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000";
const LEADING = new RegExp(`^[${SPACE}]+`);
const TRAILING = new RegExp(`[${SPACE}]+$`);

export function trimSpace(s: string): string {
  return s.replace(LEADING, "").replace(TRAILING, "");
}

/** TOP is the anchor before the first paragraph. */
export const TOP = -1;

export function paragraphsOf(body: string): Paragraph[] {
  const out: Paragraph[] = [];
  let offset = 0;
  for (const raw of body.split("\n")) {
    const lineStart = offset;
    offset += raw.length + 1;
    const text = trimSpace(raw);
    if (text === "") continue;
    const start = lineStart + raw.indexOf(text);
    out.push({ index: out.length, text, raw, start, end: start + text.length });
  }
  return out;
}

/**
 * contextOf is the paragraph either side, for a sheet that shows the author
 * where a picture is going without reprinting the chapter.
 */
export function contextOf(
  paragraphs: Paragraph[],
  index: number,
): { before: string; at: string; after: string } {
  if (index === TOP) {
    return { before: "", at: "", after: paragraphs[0]?.text ?? "" };
  }
  return {
    before: paragraphs[index - 1]?.text ?? "",
    at: paragraphs[index]?.text ?? "",
    after: paragraphs[index + 1]?.text ?? "",
  };
}
